import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';
import { FormField, Button, Alert, Card } from '../ui';
import { PasswordField } from '../ui/forms';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, formData.password);
    if (result.success) {
      setMessage('Password reset successful! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } else {
      setError(result.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900 dark:to-primary-800 px-4 py-8 sm:py-12">
        <div className="max-w-md w-full">
          <Card>
            <div className="text-center">
              <Alert variant="error" className="mb-4">
                Invalid reset token
              </Alert>
              <Link
                to="/forgot-password"
                className="text-secondary-600 dark:text-secondary-400 font-medium hover:text-secondary-700 dark:hover:text-secondary-300 hover:underline transition-colors"
              >
                Request a new reset link
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900 dark:to-primary-800 px-4 py-8 sm:py-12">
      <div className="max-w-md w-full">
        <Card className="animate-fade-in">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <Logo className="h-10 sm:h-12 w-auto" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Reset Password
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-2 text-sm sm:text-base">
              Enter your new password
            </p>
          </div>

          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          {message && (
            <Alert variant="success" className="mb-4">
              {message}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            <PasswordField
              label="New Password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="••••••••"
              helperText="Must be at least 8 characters with uppercase, lowercase, and number"
            />

            <PasswordField
              label="Confirm Password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="••••••••"
            />

            <Button
              type="submit"
              disabled={loading}
              loading={loading}
              variant="primary"
              size="lg"
              className="w-full"
            >
              Reset Password
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-secondary-600 dark:text-secondary-400 font-medium hover:text-secondary-700 dark:hover:text-secondary-300 hover:underline transition-colors"
            >
              Back to login
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;

